import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import { Typography, Card, CardContent, TextField, Grid } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Page, Text, View, Document, PDFViewer } from "@react-pdf/renderer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController, useGlobalMDAlert } from "context";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import {
  FP,
  PageHeader,
  StatCard,
  StatusPill,
  fpInputSx,
} from "components/financepro";
import { postDO } from "modules/dataorbit/common";
import { useApi } from "lib/api";

const parseNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).split(",").join(""));
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v) =>
  `$${parseNumber(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// 5 buckets fijos por rango de días de atraso.
const BUCKETS = [
  { key: "b1_30",   label: "1-30 días",       min: 1,   max: 30,        variant: "info",    accent: FP.color.accentBlue },
  { key: "b31_60",  label: "31-60 días",      min: 31,  max: 60,        variant: "warning", accent: FP.color.warning },
  { key: "b61_90",  label: "61-90 días",      min: 61,  max: 90,        variant: "warning", accent: "#D97706" },
  { key: "b91_120", label: "91-120 días",     min: 91,  max: 120,       variant: "danger",  accent: "#DC2626" },
  { key: "b120",    label: "Más de 120 días", min: 121, max: Infinity,  variant: "danger",  accent: FP.color.danger },
];

// Construye el link de WhatsApp a partir del celular. Limpia caracteres no
// numéricos y antepone el código de país (1 = RD) cuando faltan dígitos.
// Incluye un mensaje predeterminado con los datos de la deuda del cliente.
function buildWhatsappLink(row, negocio) {
  let digits = String(row?.celular || "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length === 10) digits = "1" + digits;

  const nombre = (row?.cliente || "").trim();
  const saludo = nombre ? `Estimado/a ${nombre}` : "Estimado/a cliente";
  const mensaje =
    `${saludo}, le saludamos de ${negocio || "nuestra empresa"}.\n\n` +
    `Le recordamos que su préstamo #${row?.id} presenta un atraso de ${row?.dias_atraso} día(s), ` +
    `con un monto vencido de ${fmtMoney(row?.atraso_total)} (La mora no esta incluida).\n\n` +
    `Le agradecemos comunicarse con nosotros para regularizar su situación. ¡Gracias!`;

  return `https://wa.me/${digits}?text=${encodeURIComponent(mensaje)}`;
}

function getBucketForDias(diasRaw) {
  const dias = parseNumber(diasRaw);
  for (const b of BUCKETS) {
    if (dias >= b.min && dias <= b.max) return b;
  }
  return null;
}

// Normaliza una fila del backend a un shape uniforme. El endpoint
// /api/reportes_morosidad devuelve por fila:
//   id_prestamo, identificacion, cliente_nombre,
//   max_days_overdue, capital_overdue, interest_overdue, current_amount
function normalizeRow(raw, idx) {
  const id = raw.id_prestamo ?? raw.id ?? `row-${idx}`;
  const cedula = raw.identificacion ?? raw.cedula ?? "";
  const celular = raw.celular ?? raw.telefono ?? "";
  const cliente = raw.cliente_nombre ?? [raw.nombres, raw.apellidos].filter(Boolean).join(" ");
  const dias = parseNumber(raw.max_days_overdue ?? raw.dias_atraso);
  const capVenc = parseNumber(raw.capital_overdue);
  const intVenc = parseNumber(raw.interest_overdue);
  const corriente = parseNumber(raw.current_amount);
  const estado_producto = raw.estado_producto ?? "";
  return {
    id,
    cedula,
    celular,
    cliente,
    estado_producto,
    dias_atraso: dias,
    capital_overdue: capVenc,
    interest_overdue: intVenc,
    current_amount: corriente,
    atraso_total: capVenc + intVenc,
  };
}

function ReporteMorosidad() {
  const [nosucursal, setNosucursal] = useState(null);
  const [sucursal, setSucursal] = useState([]);

  const [rowsRaw, setRowsRaw] = useState([]);
  const [presentarpdf, setPresentarpdf] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [enviandoDO, setEnviandoDO] = useState(null);

  const toggleBucket = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  const expandAll = () => setCollapsed({});
  const collapseAll = () => setCollapsed(Object.fromEntries(BUCKETS.map((b) => [b.key, true])));

  const [controller] = useMaterialUIController();
  const { nombreNegocio, usuarioLog, claveLog } = controller;
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    post("/api/recuperasucursales").then(({ status, data }) => {
      if (status === 200) {
        setSucursal(data["sucursales"] || []);
      }
      if (status === 400) {
        setSucursal([]);
        showAlert({ message: data["Error"], color: "error", autoHideDuration: 3000 });
      }
    });
  }, []);

  const rows = useMemo(() => {
    const norm = (rowsRaw || []).map((r, i) => normalizeRow(r, i));
    // sólo morosos (días > 0) — la BD ya devuelve sólo activos con cuotas pendientes,
    // pero igual filtramos cualquier "al día" que se cuele
    return norm.filter((r) => r.dias_atraso > 0);
  }, [rowsRaw]);

  // Agrupa las filas por bucket en el orden definido.
  const grupos = useMemo(() => {
    const map = Object.fromEntries(BUCKETS.map((b) => [b.key, []]));
    rows.forEach((r) => {
      const b = getBucketForDias(r.dias_atraso);
      if (b) map[b.key].push(r);
    });
    BUCKETS.forEach((b) => {
      map[b.key].sort((a, z) => z.dias_atraso - a.dias_atraso);
    });
    return BUCKETS.map((b) => ({
      ...b,
      items: map[b.key],
      count: map[b.key].length,
      totalAtraso: map[b.key].reduce((acc, r) => acc + r.atraso_total, 0),
      totalCapital: map[b.key].reduce((acc, r) => acc + r.capital_overdue, 0),
      totalInteres: map[b.key].reduce((acc, r) => acc + r.interest_overdue, 0),
    }));
  }, [rows]);

  function procesarrequerimiento() {
    setCargando(true);
    post("/api/reportes_morosidad", { sucursal: nosucursal })
      .then(({ status, data }) => {
        if (status === 200) {
          const items = data["data"] || data["morosos"] || data["morosidad"] || [];
          setRowsRaw(items);
          if (items.length === 0) {
            showAlert({ message: "No hay clientes morosos", color: "info", autoHideDuration: 3000 });
          }
        } else {
          setRowsRaw([]);
          showAlert({ message: data["Error"] || "Error al generar reporte", color: "error", autoHideDuration: 3000 });
        }
      })
      .catch(() => {
        setRowsRaw([]);
        showAlert({ message: "Error de red al generar reporte", color: "error", autoHideDuration: 3000 });
      })
      .finally(() => setCargando(false));
  }

  // Envia a la red DataOrbit los prestamos indicados (uno o toda una categoria).
  // `key` identifica el boton en curso para deshabilitarlo mientras envia.
  function enviarADataOrbit(noprests, key) {
    if (!noprests || noprests.length === 0) return;
    setEnviandoDO(key);
    postDO("/api/dataorbit_enviar_prestamos", {
      usuario: usuarioLog,
      password: claveLog,
      nombrenegocio: nombreNegocio,
      noprests,
    })
      .then(({ ok, status, data }) => {
        if (ok) {
          showAlert({ message: `Se enviaron ${data.enviados} préstamo(s) a DataOrbit`, color: "success", autoHideDuration: 3000 });
        } else if (status === 403) {
          showAlert({ message: "Primero únete a DataOrbit desde su módulo", color: "warning", autoHideDuration: 4000 });
        } else {
          showAlert({ message: data.Error || "No se pudo enviar a DataOrbit", color: "error", autoHideDuration: 3000 });
        }
      })
      .catch(() => showAlert({ message: "Error de red al enviar a DataOrbit", color: "error", autoHideDuration: 3000 }))
      .finally(() => setEnviandoDO(null));
  }

  const totalMorosos = rows.length;
  const totalCapitalVenc = rows.reduce((acc, r) => acc + r.capital_overdue, 0);
  const totalInteresVenc = rows.reduce((acc, r) => acc + r.interest_overdue, 0);
  const totalAtraso = totalCapitalVenc + totalInteresVenc;
  const promedioDias = rows.length
    ? Math.round(rows.reduce((acc, r) => acc + r.dias_atraso, 0) / rows.length)
    : 0;

  function handlepdf() {
    if (rows.length === 0) {
      showAlert({ message: "No hay datos para procesar este reporte", color: "error", autoHideDuration: 3000 });
    } else {
      setPresentarpdf(true);
    }
  }

  const MyDocument = () => (
    <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%" }}>
      <Document>
        <Page size="A4" orientation="landscape">
          <View style={{ textAlign: "center", width: "94%", fontSize: 14, textTransform: "uppercase", marginTop: 15 }} fixed>
            <Text>{nombreNegocio}</Text>
          </View>
          <View style={{ textAlign: "center", width: "94%", fontSize: 9, textTransform: "uppercase" }} fixed>
            <Text>Reporte de Morosidad por Bucket</Text>
          </View>
          <View style={{ textAlign: "center", width: "94%", fontSize: 9 }} fixed>
            <Text>  </Text>
          </View>

          {grupos.filter((g) => g.count > 0).map((g) => (
            <View key={g.key} wrap={false} style={{ marginLeft: 15, marginBottom: 8, width: "94%" }}>
              <View style={{ flexDirection: "row", alignItems: "center", backgroundColor: "#E6EAF1", padding: 4, marginBottom: 2 }}>
                <Text style={{ flex: 1, fontSize: 10, fontWeight: "bold" }}>{g.label}</Text>
                <Text style={{ width: 70, fontSize: 9, textAlign: "right" }}>Clientes: {g.count}</Text>
                <Text style={{ width: 130, fontSize: 9, textAlign: "right" }}>Atraso: {fmtMoney(g.totalAtraso)}</Text>
              </View>
              <View style={{ flexDirection: "row", height: 18, fontSize: 8, backgroundColor: "#F4F6FB", color: "black" }}>
                <Text style={{ width: 55, padding: 2 }}>Prest#</Text>
                <Text style={{ width: 80, padding: 2 }}>Cédula</Text>
                <Text style={{ width: 170, padding: 2 }}>Cliente</Text>
                <Text style={{ width: 80, padding: 2, textAlign: "right" }}>Cap. venc.</Text>
                <Text style={{ width: 80, padding: 2, textAlign: "right" }}>Int. venc.</Text>
                <Text style={{ width: 85, padding: 2, textAlign: "right" }}>Atraso</Text>
                <Text style={{ width: 80, padding: 2, textAlign: "right" }}>Corriente</Text>
                <Text style={{ width: 40, padding: 2, textAlign: "center" }}>Días</Text>
              </View>
              {g.items.map((r, i) => (
                <View key={i} style={{ flexDirection: "row", height: 13, fontSize: 8, color: "black" }}>
                  <Text style={{ width: 55, padding: 2 }}>{r.id}</Text>
                  <Text style={{ width: 80, padding: 2 }}>{r.cedula}</Text>
                  <Text style={{ width: 170, padding: 2 }}>{r.cliente}</Text>
                  <Text style={{ width: 80, padding: 2 }}>{r.estado_producto}</Text>
                  <Text style={{ width: 80, padding: 2, textAlign: "right" }}>{fmtMoney(r.capital_overdue)}</Text>
                  <Text style={{ width: 80, padding: 2, textAlign: "right" }}>{fmtMoney(r.interest_overdue)}</Text>
                  <Text style={{ width: 85, padding: 2, textAlign: "right" }}>{fmtMoney(r.atraso_total)}</Text>
                  <Text style={{ width: 80, padding: 2, textAlign: "right" }}>{fmtMoney(r.current_amount)}</Text>
                  <Text style={{ width: 40, padding: 2, textAlign: "center" }}>{r.dias_atraso}</Text>
                </View>
              ))}
            </View>
          ))}

          <View style={{ marginLeft: 15, marginTop: 8, width: "94%", flexDirection: "row", backgroundColor: "#E6EAF1", padding: 4 }}>
            <Text style={{ flex: 1, fontSize: 10, fontWeight: "bold" }}>TOTAL GENERAL</Text>
            <Text style={{ width: 80, fontSize: 9, textAlign: "right" }}>Clientes: {totalMorosos}</Text>
            <Text style={{ width: 160, fontSize: 9, textAlign: "right" }}>Atraso: {fmtMoney(totalAtraso)}</Text>
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Préstamos"
        subtitle="Reporte de Morosidad"
        cta={
          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            disabled={rows.length === 0}
            onClick={handlepdf}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            Export Data
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Filtros de Búsqueda
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Autocomplete
                  disablePortal
                  options={sucursal || []}
                  value={nosucursal}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option?.label || "";
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const a = typeof option === "string" ? option : option?.label;
                    const b = typeof value === "string" ? value : value?.label;
                    return a === b;
                  }}
                  onChange={(_, value) => setNosucursal(value || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                disabled={cargando}
                startIcon={<SearchIcon />}
                onClick={procesarrequerimiento}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
                }}
              >
                {cargando ? "Procesando..." : "Procesar"}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <>
            <Box
              sx={{
                display: "grid",
                width: "100%",
                gap: 2,
                mb: 3,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
              }}
            >
              <StatCard label="Clientes morosos" value={String(totalMorosos)} icon={GroupsRoundedIcon} accent={FP.color.accentBlue} />
              <StatCard label="Total en atraso" value={fmtMoney(totalAtraso)} icon={PaidRoundedIcon} accent={FP.color.danger} />
              <StatCard label="Capital vencido" value={fmtMoney(totalCapitalVenc)} icon={AccountBalanceRoundedIcon} accent={FP.color.warning} />
              <StatCard label="Días prom. atraso" value={`${promedioDias} d`} icon={WarningAmberRoundedIcon} accent={FP.color.navy} />
            </Box>

            <Box
              sx={{
                display: "grid",
                width: "100%",
                gap: 1.5,
                mb: 3,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(5, minmax(0, 1fr))",
                },
              }}
            >
              {grupos.map((g) => (
                <Box
                  key={g.key}
                  onClick={() => g.count > 0 && toggleBucket(g.key)}
                  role={g.count > 0 ? "button" : undefined}
                  tabIndex={g.count > 0 ? 0 : -1}
                  sx={{
                    border: `1px solid ${FP.color.border}`,
                    borderRadius: 2,
                    p: 1.4,
                    bgcolor: "#FFFFFF",
                    position: "relative",
                    overflow: "hidden",
                    cursor: g.count > 0 ? "pointer" : "default",
                    userSelect: "none",
                    opacity: g.count === 0 ? 0.55 : 1,
                    transition: "background-color 0.18s ease",
                    "&:hover": g.count > 0 ? { bgcolor: "#F4F6FB" } : undefined,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      bgcolor: g.accent,
                    },
                  }}
                >
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                    {g.label}
                  </Typography>
                  <Typography sx={{ color: FP.color.textPrimary, fontSize: "1.1rem", fontWeight: 800, mt: 0.4, lineHeight: 1.1 }}>
                    {g.count} <Typography component="span" sx={{ fontSize: "0.7rem", fontWeight: 600, color: FP.color.textSecondary }}>clientes</Typography>
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.78rem", fontWeight: 700, mt: 0.4, fontVariantNumeric: "tabular-nums" }}>
                    {fmtMoney(g.totalAtraso)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Button
                size="small"
                onClick={expandAll}
                sx={{
                  color: FP.color.textSecondary,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
                }}
              >
                Expandir todo
              </Button>
              <Button
                size="small"
                onClick={collapseAll}
                sx={{
                  color: FP.color.textSecondary,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
                }}
              >
                Colapsar todo
              </Button>
            </Box>
          </>
        )}

        {rows.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: "hidden",
            }}
          >
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                  Seleccione la sucursal y haga clic en &quot;Procesar&quot; para obtener resultados.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          grupos.filter((g) => g.count > 0).map((g) => {
            const isCollapsed = !!collapsed[g.key];
            return (
            <Card
              key={g.key}
              elevation={0}
              sx={{
                mb: 2.5,
                borderRadius: 2.5,
                border: `1px solid ${FP.color.border}`,
                bgcolor: FP.color.cardBg,
                boxShadow: FP.shadow.soft,
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => toggleBucket(g.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleBucket(g.key);
                  }
                }}
                aria-expanded={!isCollapsed}
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 1.4,
                  borderBottom: isCollapsed ? "none" : `1px solid ${FP.color.border}`,
                  bgcolor: "#FAFBFE",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  position: "relative",
                  cursor: "pointer",
                  userSelect: "none",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    bgcolor: g.accent,
                  },
                  "&:hover": { bgcolor: "#F4F6FB" },
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
                    {g.label}
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.74rem", fontWeight: 600 }}>
                    {g.count} {g.count === 1 ? "cliente" : "clientes"} · Atraso {fmtMoney(g.totalAtraso)}
                  </Typography>
                </Box>
                <StatusPill label={`${g.count}`} variant={g.variant} />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={enviandoDO === `cat-${g.key}`}
                  startIcon={<HubRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    enviarADataOrbit(g.items.map((it) => it.id), `cat-${g.key}`);
                  }}
                  sx={{
                    borderColor: FP.color.navy,
                    color: FP.color.navy,
                    borderRadius: 1.2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    px: 1.2,
                    "&:hover": { bgcolor: FP.color.accentBlueSoft, borderColor: FP.color.navy },
                  }}
                >
                  {enviandoDO === `cat-${g.key}` ? "Enviando..." : "Enviar categoría"}
                </Button>
                <IconButton
                  size="small"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleBucket(g.key);
                  }}
                  aria-label={isCollapsed ? "Expandir" : "Colapsar"}
                  sx={{
                    color: FP.color.textSecondary,
                    transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                >
                  <ExpandMoreRoundedIcon />
                </IconButton>
              </Box>
              {!isCollapsed && (
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0,1fr))" },
                    gap: 1.4,
                  }}
                >
                  {g.items.map((row, idx) => (
                    <Box
                      key={`${row.id}-${idx}`}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: "#FFFFFF",
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1rem", lineHeight: 1.2 }}>
                            Préstamo #{row.id}
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.85rem", fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}>
                            {row.cliente || "—"}
                          </Typography>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem", mt: 0.1 }}>
                            {row.cedula || "—"}
                          </Typography>
                          <Typography sx={{ color: "red", fontSize: "0.75rem", mt: 0.1 }}> 
                            {row.estado_producto=="I"?"Incautado":"-"}
                          </Typography>
                        </Box>
                        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.6 }}>
                          <StatusPill label={`${row.dias_atraso} d`} variant={g.variant} />
                          {buildWhatsappLink(row, nombreNegocio) && (
                            <Button
                              size="small"
                              component="a"
                              href={buildWhatsappLink(row, nombreNegocio)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              startIcon={<WhatsAppIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                bgcolor: "#25D366",
                                color: "#FFFFFF",
                                borderRadius: 1.2,
                                px: 1.2,
                                py: 0.3,
                                minWidth: 0,
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                boxShadow: "none",
                                "&:hover": { bgcolor: "#1EBE57", boxShadow: "none" },
                              }}
                            >
                              WhatsApp
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={enviandoDO === `prest-${row.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              enviarADataOrbit([row.id], `prest-${row.id}`);
                            }}
                            startIcon={<HubRoundedIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              borderColor: FP.color.navy,
                              color: FP.color.navy,
                              borderRadius: 1.2,
                              px: 1.2,
                              py: 0.3,
                              minWidth: 0,
                              textTransform: "none",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              "&:hover": { bgcolor: FP.color.accentBlueSoft, borderColor: FP.color.navy },
                            }}
                          >
                            {enviandoDO === `prest-${row.id}` ? "Enviando..." : "DataOrbit"}
                          </Button>
                        </Box>
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 1 }}>
                        {[
                          ["Atraso total", fmtMoney(row.atraso_total)],
                          ["Cap. venc.", fmtMoney(row.capital_overdue)],
                          ["Int. venc.", fmtMoney(row.interest_overdue)],
                          ["Al corriente", fmtMoney(row.current_amount)],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                              {label}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.92rem", fontWeight: 600, fontVariantNumeric: "tabular-nums", wordBreak: "break-word" }}>
                              {value ?? "—"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
              )}
            </Card>
            );
          })
        )}

        {rows.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" }, mt: 1 }}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlepdf}
              sx={{
                width: { xs: "100%", sm: "auto" },
                borderColor: FP.color.danger,
                color: FP.color.danger,
                borderRadius: 1.2,
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
              }}
            >
              Generar PDF
            </Button>
          </Box>
        )}

        {presentarpdf === true && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa del Reporte de Morosidad
              </Typography>
              <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
                <CancelIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              <MyDocument />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}

export default ReporteMorosidad;
